import React, { useState } from 'react';
import { Box } from '@mui/material';
import type { StatType } from '../statsBar/StatsBar';

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

interface SwipeHandleProps {
  activeStatType: StatType;
  onStatClick: (statType: StatType) => void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  onDragChange?: (isDragging: boolean, dragOffset: number) => void;
}

export const SwipeHandle: React.FC<SwipeHandleProps> = ({ 
  activeStatType, 
  onStatClick,
  isExpanded = false,
  onExpandToggle,
  onDragChange
}) => {
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [touchEnd, setTouchEnd] = useState<TouchPoint | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Prevent pull-to-refresh when handling swipe gestures
    e.preventDefault();
    
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchEnd(null);
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    // Prevent pull-to-refresh during swipe gestures
    e.preventDefault();
    
    const touch = e.touches[0];
    const currentTouchEnd = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    
    setTouchEnd(currentTouchEnd);
    
    // Calculate drag offset in real-time
    const deltaY = currentTouchEnd.y - touchStart.y; // Fixed: now positive when dragging down
    
    // Start dragging if moved more than 5px
    if (Math.abs(deltaY) > 5) {
      setIsDragging(true);
      
      // Apply drag offset with resistance, but don't change state during drag
      let dragOffset = deltaY * 0.6;
      
      // If already expanded and trying to drag up further, apply heavy resistance/limit
      if (isExpanded && deltaY < 0) {
        // Allow only minimal upward movement with heavy resistance when already expanded
        dragOffset = Math.max(deltaY * 0.1, -20); // Max 20px upward movement with heavy resistance
      }
      
      // Notify parent of drag state change for visual feedback
      if (onDragChange) {
        onDragChange(true, dragOffset);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      // Reset drag state
      setIsDragging(false);
      setTouchStart(null);
      setTouchEnd(null);
      
      // Notify parent that dragging has ended
      if (onDragChange) {
        onDragChange(false, 0);
      }
      return;
    }

    const deltaY = touchStart.y - touchEnd.y;
    const deltaTime = touchEnd.time - touchStart.time;
    const minSwipeDistance = 30; // Minimum swipe distance
    const maxSwipeTime = 800; // Maximum swipe time

    // Only process if it was actually a drag gesture
    if (isDragging && Math.abs(deltaY) > minSwipeDistance && deltaTime < maxSwipeTime) {
      if (deltaY > 0) {
        // Swipe up - Scenario B -> C
        if (activeStatType && !isExpanded && onExpandToggle) {
          onExpandToggle();
        }
      } else if (deltaY < 0) {
        // Swipe down
        if (activeStatType && isExpanded && onExpandToggle) {
          // Scenario C -> B: Panel expanded -> collapse to regular
          onExpandToggle();
        } else if (activeStatType && !isExpanded) {
          // Scenario B -> A: Panel regular -> close completely          onStatClick(null);
        }
      }
    } else if (!isDragging && Math.abs(deltaY) < 5) {
      // Tap gesture - toggle between regular and fullscreen
      if (activeStatType && onExpandToggle) {
        onExpandToggle();
      }
    }

    // Reset all drag state
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
    
    // Notify parent that dragging has ended
    if (onDragChange) {
      onDragChange(false, 0);
    }
  };

  // Position in flex layout above StatsBar
  return (
    <Box
      className="swipe-handle"
      sx={{
        width: '100%',
        height: '8px', // Much thinner - reduced from 25px to 8px
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        touchAction: 'none', // Prevent all default touch behaviors including pull-to-refresh
        backgroundColor: 'var(--color-light-gray)', // Match stats tiles background
        borderTop: '1px solid var(--color-neutral)', // Match stats tiles border
        flexShrink: 0, // Don't shrink in flex layout
        '&:hover': {
          backgroundColor: 'var(--color-light-gray)', // Keep same color on hover
        },
      }}
      onTouchStart={onDragChange ? handleTouchStart : undefined}
      onTouchMove={onDragChange ? handleTouchMove : undefined}
      onTouchEnd={onDragChange ? handleTouchEnd : undefined}
      onClick={() => {
        // Handle click for expand/collapse or open panel
        if (activeStatType && onExpandToggle) {
          onExpandToggle();
        } else if (!activeStatType) {
          onStatClick('distance');
        }
      }}
    >
      {/* Visual indicator for swipe area */}
      <Box
        sx={{
          width: activeStatType ? '24px' : '32px', // Smaller indicator
          height: '3px', // Thinner indicator to match the thinner handle
          backgroundColor: activeStatType ? 'var(--color-primary)' : 'var(--color-neutral)', // Use CSS custom properties
          borderRadius: '1.5px',
          opacity: activeStatType ? 0.8 : 0.6,
          boxShadow: 'none', // Remove shadow for cleaner look
        }}
      />
    </Box>
  );
};
